"use client";

import { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Link,
  Typography,
  styled,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import toast from "react-hot-toast";
import { SkillLogo } from "@components/shared/SkillLogo";
import {
  Card,
  Tag,
  Avatar,
  Alert,
  Dialog,
  DialogPortal,
  DialogBackdrop,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogClose,
} from "@/ui";
import ImproveAIButton from "@components/shared/form/ai/ImproveAIButton";
import useGenerateCandidateSummary from "@lib/candidates/useGenerateCandidateSummary";
import SummaryLoading from "./SummaryLoading";

const Header = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: theme.spacing(2),
  marginBottom: theme.spacing(2),
}));

const Section = styled(Box)({
  marginTop: "48px",
});

const SectionTitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(1),
}));

const CardStack = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1.5),
}));

// Column count is capped at 4 but never exceeds the actual number of cards,
// so 1-3 pitch angles fill the row's full width instead of leaving an empty
// trailing column where a 4th card would go.
const PitchAngleGrid = styled(Box, {
  shouldForwardProp: (prop) => prop !== "count",
})(({ theme, count }) => ({
  display: "grid",
  gridTemplateColumns: `repeat(${Math.min(count, 4)}, 1fr)`,
  gap: theme.spacing(1.5),
  [theme.breakpoints.down("md")]: {
    gridTemplateColumns: `repeat(${Math.min(count, 2)}, 1fr)`,
  },
  [theme.breakpoints.down("sm")]: {
    gridTemplateColumns: "1fr",
  },
}));

const NumberBadge = styled(Box)(({ theme }) => ({
  width: 24,
  height: 24,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "0.75rem",
  fontWeight: 700,
  color: "#FFFFFF",
  backgroundColor: theme.palette.primary.main,
  marginBottom: theme.spacing(1),
}));

const ChipRow = styled(Box)(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  gap: theme.spacing(1),
}));

const EmptyState = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: theme.spacing(2),
  padding: theme.spacing(6, 2),
  textAlign: "center",
}));

function relativeTime(iso) {
  if (!iso) {
    return "";
  }
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) {
    return "";
  }
  const days = Math.floor((Date.now() - then) / 86400000);
  if (days <= 0) {
    return "Updated today";
  }
  if (days === 1) {
    return "Updated 1d ago";
  }
  return `Updated ${days}d ago`;
}

function citationUrl(c) {
  if (typeof c === "string") {
    return c;
  }
  return c?.url || c?.link || "";
}

function citationLabel(c) {
  if (typeof c === "string") {
    return c;
  }
  return c?.title || c?.url || c?.link || "";
}

export default function SummaryTab({ candidate, isGeneratingSummary }) {
  const s = candidate?.internal_summary;
  const mutation = useGenerateCandidateSummary(candidate?.uuid);
  const [citationsOpen, setCitationsOpen] = useState(false);
  const loadingToastId = useRef(null);

  const citations = Array.isArray(s?.citations) ? s.citations : [];

  useEffect(
    () => () => {
      if (loadingToastId.current) {
        toast.dismiss(loadingToastId.current);
        loadingToastId.current = null;
      }
    },
    []
  );

  const handleGenerate = () => {
    const toastId = toast.loading(
      s ? "Regenerating summary..." : "Generating summary..."
    );
    loadingToastId.current = toastId;
    mutation.mutate(undefined, {
      onSuccess: () => {
        toast.dismiss(toastId);
        loadingToastId.current = null;
        toast.success("Summary ready");
      },
      onError: (error) => {
        toast.dismiss(toastId);
        loadingToastId.current = null;
        toast.error(error?.message || "Failed to generate summary");
      },
    });
  };

  const skills = Array.isArray(candidate?.skills) ? candidate.skills : [];

  // The auto-summary is generated asynchronously after a CV import completes, so
  // a freshly-imported candidate has no summary yet but one is on the way. Two
  // signals tell us to wait:
  //   - `summary_pending`: an authoritative server flag set when the import
  //     dispatches the generation job and cleared when it finishes/fails. This
  //     survives navigation and reloads, so it covers the common case of opening
  //     the candidate moments after import (the in-flight job is no longer in the
  //     client jobs list, so `isGeneratingSummary` is already false here).
  //   - `isGeneratingSummary`: a best-effort, jobs-list flag that covers watching
  //     the page while the import itself is still running.
  // Guard both with `!s`: once a summary exists we never show the loader, so a
  // stale `summary_pending` can't trap the tab in a spinner. `mutation.isPending`
  // handles a manual Refresh.
  const summaryPending = Boolean(candidate?.summary_pending);
  const isLoadingSummary =
    ((isGeneratingSummary || summaryPending) && !s) || mutation.isPending;

  return (
    <Box>
      {isLoadingSummary ? (
        <SummaryLoading />
      ) : !s ? (
        <EmptyState>
          <Typography variant="body1" color="text.secondary">
            No summary generated yet.
          </Typography>
          <Button
            variant="outlined"
            startIcon={
              mutation.isPending ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <AutoAwesomeIcon />
              )
            }
            onClick={handleGenerate}
            disabled={mutation.isPending}
          >
            Generate summary
          </Button>
        </EmptyState>
      ) : (
        <Box>
          <Header>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  textTransform: "uppercase",
                  fontSize: "0.7rem",
                  letterSpacing: "0.04em",
                }}
              >
                {relativeTime(s.generated_at)}
              </Typography>
            </Box>
            <ImproveAIButton
              label="Refresh summary"
              active={mutation.isPending}
              onClick={handleGenerate}
            />
          </Header>

          <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
            {s.narrative}
          </Typography>

          {s.pitch_angles?.length > 0 && (
            <Section>
              <SectionTitle variant="h3">Pitch Angles</SectionTitle>
              <PitchAngleGrid count={s.pitch_angles.length}>
                {s.pitch_angles.map((item, i) => (
                  <Card key={i} padding="sm" variant="flat" style={{ background: "#EDF4FE" }}>
                    <NumberBadge>{i + 1}</NumberBadge>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {item}
                    </Typography>
                  </Card>
                ))}
              </PitchAngleGrid>
            </Section>
          )}

          {s.best_fit_role_types?.length > 0 && (
            <Section>
              <SectionTitle variant="h3">Best-fit Role Types</SectionTitle>
              <ChipRow>
                {s.best_fit_role_types.map((role, i) => (
                  <Tag key={i} color="info">
                    {role}
                  </Tag>
                ))}
              </ChipRow>
            </Section>
          )}

          {s.suggested_companies?.length > 0 && (
            <Section>
              <SectionTitle variant="h3">Suggested Companies</SectionTitle>
              <CardStack>
                {s.suggested_companies.map((c, i) => (
                  <Card key={i} padding="sm">
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <Avatar name={c.company} variant="square" size="md" />
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {c.company}
                        </Typography>
                        {c.reason && (
                          <Typography variant="body2" color="text.secondary">
                            {c.reason}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Card>
                ))}
              </CardStack>
            </Section>
          )}

          {s.personalisation?.length > 0 && (
            <Section>
              <SectionTitle variant="h3">Personalisation for Outreach</SectionTitle>
              <Alert variant="info">
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {s.personalisation.map((item, i) => (
                    <Box key={i} sx={{ display: "flex", gap: 1 }}>
                      <span aria-hidden="true">→</span>
                      <Typography variant="body2">{item}</Typography>
                    </Box>
                  ))}
                </Box>
              </Alert>
            </Section>
          )}

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 3 }}
          >
            From public web sources ·{" "}
            {citations.length > 0 ? (
              <Link
                component="button"
                type="button"
                variant="caption"
                onClick={() => setCitationsOpen(true)}
              >
                {citations.length} citations
              </Link>
            ) : (
              "0 citations"
            )}
          </Typography>

          <Dialog
            open={citationsOpen}
            onOpenChange={(o) => { if (!o) setCitationsOpen(false); }}
          >
            <DialogPortal>
              <DialogBackdrop />
              <DialogContent size="sm">
                <DialogHeader>
                  <DialogTitle>Sources</DialogTitle>
                  <DialogClose />
                </DialogHeader>
                <DialogBody>
                  <Box
                    component="ol"
                    sx={{
                      m: 0,
                      pl: 3,
                      display: "flex",
                      flexDirection: "column",
                      gap: 1.5,
                    }}
                  >
                    {citations.map((c, i) => {
                      const url = citationUrl(c);
                      return (
                        <li key={i}>
                          {url ? (
                            <Link
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                              variant="body2"
                              sx={{ wordBreak: "break-all" }}
                            >
                              {citationLabel(c)}
                            </Link>
                          ) : (
                            <Typography variant="body2">
                              {citationLabel(c)}
                            </Typography>
                          )}
                        </li>
                      );
                    })}
                  </Box>
                </DialogBody>
              </DialogContent>
            </DialogPortal>
          </Dialog>
        </Box>
      )}

      <Divider sx={{ my: 4 }} />

      <Typography variant="h2">Skills</Typography>
      {skills.length > 0 ? (
        <ChipRow sx={{ mt: 2 }}>
          {skills.map((skill) => (
            <Tag
              key={skill.id}
              variant="skill"
              icon={
                skill.logo && (
                  <SkillLogo
                    sx={{ width: 20, height: 20 }}
                    src={skill.logo}
                    variant="square"
                    title={skill.name}
                    alt={skill.name}
                  />
                )
              }
            >
              {skill.name}
            </Tag>
          ))}
        </ChipRow>
      ) : (
        <Box sx={{ mt: 2, fontStyle: "italic" }}>No skills</Box>
      )}
    </Box>
  );
}
